import { useCallback, useEffect, useMemo, useState } from 'react';
export const THEME_STORAGE_KEY = 'bookora.theme';
/**
 * The resolution rule, in one place.
 *
 * Exported because the bootstrap script, the hook and any consumer that reads
 * the same key must agree — two copies of "what does `'system'` mean now" is
 * exactly how a flash of the wrong theme gets reintroduced.
 */
export function resolveThemePreference(raw) {
    return raw === 'dark' ? 'dark' : 'light';
}
function readPreference(key) {
    if (typeof window === 'undefined')
        return 'light';
    try {
        return resolveThemePreference(window.localStorage.getItem(key));
    }
    catch {
        // Storage blocked (private mode, an embedded webview). Light, as always.
        return 'light';
    }
}
/**
 * Keeps `<html data-theme>` and the browser chrome in step with the resolved
 * mode, so `@bookora/design`'s CSS variables and AntD agree.
 */
export function useThemeMode(storageKey = THEME_STORAGE_KEY) {
    const [preference, setPreferenceState] = useState(() => readPreference(storageKey));
    const mode = preference;
    useEffect(() => {
        if (typeof document === 'undefined')
            return;
        const root = document.documentElement;
        // Always explicit. Never absent, so no stylesheet's
        // `:root:not([data-theme])` branch can ever apply.
        root.setAttribute('data-theme', mode);
        root.setAttribute('lang', 'fa');
        root.setAttribute('dir', 'rtl');
        root.style.colorScheme = mode;
    }, [mode]);
    const setPreference = useCallback((next) => {
        const resolved = resolveThemePreference(next);
        setPreferenceState(resolved);
        try {
            window.localStorage.setItem(storageKey, resolved);
        }
        catch {
            /* storage blocked — the choice still applies for this page load */
        }
    }, [storageKey]);
    const toggle = useCallback(() => {
        setPreference(mode === 'dark' ? 'light' : 'dark');
    }, [mode, setPreference]);
    return useMemo(() => ({ preference, mode, setPreference, toggle }), [preference, mode, setPreference, toggle]);
}
/**
 * A blocking inline script for `<head>`, so `data-theme` is on `<html>` before
 * the first paint.
 *
 * Deliberately tiny and deliberately total: any read that throws, and any
 * stored value that is not exactly `'dark'`, ends as light. It writes the
 * attribute and `color-scheme` and nothing else — in particular it never
 * writes back to storage, for the reason in this module's header.
 *
 * ```astro
 * <script is:inline set:html={themeBootstrapScript()} />
 * ```
 * ```tsx
 * <script dangerouslySetInnerHTML={{ __html: themeBootstrapScript() }} />
 * ```
 */
export function themeBootstrapScript(storageKey = THEME_STORAGE_KEY) {
    const key = JSON.stringify(storageKey);
    return (`(function(){try{var v=localStorage.getItem(${key});` +
        `var m=v==="dark"?"dark":"light";` +
        `var e=document.documentElement;` +
        `e.setAttribute("data-theme",m);e.setAttribute("lang","fa");e.setAttribute("dir","rtl");` +
        `e.style.colorScheme=m;}catch(_){` +
        `document.documentElement.setAttribute("data-theme","light");}})();`);
}
//# sourceMappingURL=theme-mode.js.map